import { useEffect, useState } from 'react';

import { type CategoryFilterDepartments } from '../aliases';
import { useCategoryFilters } from '../useCategoryFilters';

import { useSearchFilterAggregates } from '@/modules/search/useSearchFilterAggregates';

export function useFilterByCategoryOptions(
  sellerId?: number
): CategoryFilterDepartments {
  const { data: categoriesFilterData } = useCategoryFilters();

  const { data: aggregatesData } = useSearchFilterAggregates({ sellerId });

  // Store original category filters to persist them
  const [originalCategoryFilters, setOriginalCategoryFilters] =
    useState<CategoryFilterDepartments>([]);

  // Store original aggregates data to persist them
  const [originalGroupsAggregates, setOriginalGroupsAggregates] = useState<
    Record<string, { count: number }> | undefined
  >(undefined);

  const categoryFilters = categoriesFilterData?.data.departments || [];
  const groupsAggregates = sellerId ? aggregatesData?.data.groups : undefined;

  // Store original category filters when they first load
  useEffect(() => {
    if (categoryFilters.length > 0 && !originalCategoryFilters.length) {
      setOriginalCategoryFilters(categoryFilters);
    }
  }, [categoryFilters]);

  // Store original aggregates data when they first load
  useEffect(() => {
    if (groupsAggregates && !originalGroupsAggregates) {
      setOriginalGroupsAggregates(groupsAggregates);
    }
  }, [groupsAggregates]);

  // Use original aggregates data if available, otherwise fall back to current
  const aggregatesToUse = originalGroupsAggregates || groupsAggregates;

  if (!aggregatesToUse) {
    return categoryFilters;
  }

  // Use original category filters as base if available, otherwise fall back to current
  const baseCategoryFilters =
    originalCategoryFilters.length > 0
      ? originalCategoryFilters
      : categoryFilters;

  return (
    baseCategoryFilters
      // Takes each department e.g "Men", "Women"
      .map((department) => ({
        // For each department filter it's childrens subcategories "Tops", "Bottoms", etc
        ...department,
        children: department.children.filter((group) => {
          const aggregate = aggregatesToUse?.[group.id];
          // Only keep the subcategories that have inventory
          return aggregate && aggregate.count >= 1;
        }),
      }))
      // Remove any departments that have no subcategories available
      .filter((department) => department.children.length > 0)
  );
}
